import React, { useState } from 'react'

import { goodfellasApi } from '../../apis'
import { useAuth } from '../../contexts/AuthContext'

interface DeleteAccountModalProps {
  onClose: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  profile: any
}

/**
 * Confirmation dialog for self-service account deletion.
 *
 * Asks the user *why* they are leaving (free-text, optional) and, after an
 * explicit confirmation, calls the backend which soft-deletes (inactivates) the
 * profile and stores the reason. On success the session is cleared via
 * `logout()`, which redirects the user away from the app.
 */
export default function DeleteAccountModal({
  onClose,
  profile,
}: DeleteAccountModalProps) {
  const { logout } = useAuth()
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleDelete = async () => {
    if (!confirmed) return
    setLoading(true)
    setError(null)

    try {
      const response = await goodfellasApi.delete(
        `/public/profiles/${profile.id}`,
        {
          data: { reason: reason.trim() || undefined },
          validateStatus: () => true,
        },
      )

      if (response.status >= 200 && response.status < 300) {
        // Clears the token/cookies and redirects away from the app.
        logout()
      } else {
        setError(response.data?.message || 'Erro ao excluir a conta.')
        setLoading(false)
      }
    } catch (err) {
      console.error('Error deleting account:', err)
      setError('Erro ao excluir a conta. Tente novamente.')
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10001]">
      <div className="h-full w-full overflow-y-auto flex items-start justify-center px-4 py-16">
        <div className="bg-gray-50 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-rose-600 to-red-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Excluir Conta
                  </h2>
                  <p className="text-white/80 text-sm">
                    Esta ação não pode ser desfeita
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={loading}
                className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors disabled:opacity-50"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-8 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            )}

            <p className="text-gray-600 text-sm leading-relaxed">
              Sua conta será desativada e seu perfil deixará de aparecer no
              Goodfellas. Antes de ir, conte para nós o motivo — sua resposta nos
              ajuda a melhorar.
            </p>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Por que você está excluindo sua conta?
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                maxLength={1000}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900"
                placeholder="Conte um pouco sobre o motivo (opcional)..."
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">
                Entendo que esta ação é permanente e que minha conta será
                desativada.
              </span>
            </label>

            <div className="flex gap-4 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-6 py-4 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-medium disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading || !confirmed}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-rose-600 to-red-600 text-white rounded-xl hover:from-rose-700 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-lg shadow-red-500/25"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                )}
                {loading ? 'Excluindo...' : 'Excluir minha conta'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
